import { Alert } from "components/common/Alert/Alert";
import { AppContext } from "context/app";
import { useTranslationContext } from "context/translation";
import { getErrors } from "helpers/cart";
import { useCartAnalytics } from "hooks/analytics/useCartAnalytics";
import { FC, useCallback, useContext, useEffect, useState } from "react";
import { CartPageQuery } from "services/datocms/generated";
import { CartFragment } from "services/graphql/generated";
import { logCartLineItemQtyChangedEvent } from "../../helpers/analyticsLogger";
import { BundleItem } from "./BundleItem";
import { CartHeader } from "./CartHeader";
import { CmsProductGtinWithSlug, UpsellProduct } from "./common/types";
import { CartItem } from "./item-children/CartItem/CartItem";
import { CartSummary } from "./item-children/Summary";

export interface CartProps {
  blockedItems?: string[];
  cart: CartFragment;
  checkoutImages: NonNullable<
    NonNullable<CartPageQuery>["cartPage"]
  >["checkoutPaymentImages"];
  cmsProducts?: CmsProductGtinWithSlug[];
  currentUserId: string | null;
  freeShippingThreshold?: number | null;
  goToCheckout: () => void;
  itemImgCdn?: string;
  maxCartQty: number;
  minPurchaseAmt?: number;
  minPurchaseQty?: number;
  productsImgCdnEndpoint: string;
  size: number | undefined;
  upsellItems?: UpsellProduct[];
}

export const Cart: FC<CartProps> = ({
  blockedItems,
  cart,
  checkoutImages,
  cmsProducts,
  currentUserId,
  freeShippingThreshold = 0,
  goToCheckout,
  maxCartQty,
  minPurchaseAmt = 0,
  minPurchaseQty = 0,
  productsImgCdnEndpoint,
  size,
}) => {
  const [hasBlockedItems, setHasBlockedItems] = useState(false);
  const { tenant } = useContext(AppContext);
  const { cartDiscountWarning } = useTranslationContext();

  const currency = tenant?.checkout?.paymentOptions?.defaultCurrencyCode || "";
  const { shipping, subtotal, total } = cart;
  const minPurchaseAmtNotMet = subtotal < minPurchaseAmt;
  const minPurchaseQtyNotMet = size && size < minPurchaseQty;
  const errors = getErrors(cart);
  const isButtonDisabled =
    minPurchaseAmtNotMet ||
    minPurchaseQtyNotMet ||
    !!errors?.length ||
    hasBlockedItems;

  const { handleCartItemClickLogging } = useCartAnalytics(
    currency,
    cart,
    cmsProducts,
  );

  useEffect(() => {
    if (blockedItems) {
      setHasBlockedItems(
        cart.lineItems?.some((lineItem) => {
          const id =
            lineItem.__typename === "BundleLineItem"
              ? lineItem.bundleId
              : lineItem.__typename === "CartItem"
                ? lineItem.id
                : "";

          return blockedItems.includes(id);
        }),
      );
    }
  }, [cart, blockedItems]);

  const getBlockedItem = useCallback(
    (id: string) => {
      if (blockedItems && blockedItems.includes(id)) {
        return {
          id,
          isZipError: true,
        };
      }
      return undefined;
    },
    [blockedItems],
  );

  return (
    <>
      <CartHeader
        freeShippingThreshold={freeShippingThreshold}
        isButtonDisabled={isButtonDisabled}
        onButtonClick={goToCheckout}
        subtotal={subtotal}
        total={total}
      />
      <div className="container">
        {cartDiscountWarning && (
          <Alert type="warning">{cartDiscountWarning}</Alert>
        )}
        <div className="flex flex-col pt-10 md:pt-20 pb-12 md:pb-[100px] divide-y divide-gray-200">
          {cart?.lineItems?.map((lineItem, index) => {
            if (lineItem.__typename === "BundleLineItem") {
              return (
                <BundleItem
                  key={index}
                  lineItem={lineItem}
                  cmsProducts={cmsProducts}
                  currency={currency}
                />
              );
            } else if (lineItem.__typename === "CartItem") {
              return (
                <CartItem
                  blockedItem={getBlockedItem(lineItem.id)}
                  cmsProducts={cmsProducts}
                  currentUserId={currentUserId}
                  handleLogging={() => handleCartItemClickLogging(lineItem)}
                  itemImgCdn={productsImgCdnEndpoint}
                  itemImgSrc={lineItem.item.imgUrl || ""}
                  key={index}
                  lineItem={lineItem}
                  logCartLineItemQtyChangedEvent={
                    logCartLineItemQtyChangedEvent
                  }
                  maxCartQty={maxCartQty}
                  currency={currency}
                />
              );
            }
          })}
        </div>
      </div>
      <CartSummary
        checkoutImages={checkoutImages}
        freeShippingThreshold={freeShippingThreshold || 0}
        shippingFee={shipping}
        subtotal={subtotal}
        total={total}
      />
    </>
  );
};
